import { useCallback, useRef, useEffect } from 'react';
import _throttle from 'lodash/throttle';
import { isProviderElement } from '../utils';

const THROTTLE_DELAY = 300;

const THROTTLE_CONFIG = {
  leading: true,
  trailing: true
};

/**
 * Custom hook that encapsulates provider interaction logic including
 * throttling, event handling, and accessibility features.
 */
export const useProviderInteractions = ({
  onProviderHover,
  onKeyboardFocus,
  onKeyboardBlur,
  provider
}) => {
  const throttledFocusRef = useRef(null);
  const throttledBlurRef = useRef(null);

  const getProviderId = useCallback(() => {
    return provider && provider.id ? provider.id : null;
  }, [provider]);

  useEffect(() => {
    throttledFocusRef.current = _throttle(
      (providerId) => onKeyboardFocus?.(providerId),
      THROTTLE_DELAY,
      THROTTLE_CONFIG
    );

    throttledBlurRef.current = _throttle(
      (relatedTarget) => {
        if (!relatedTarget || !isProviderElement(relatedTarget)) {
          onKeyboardBlur?.(null);
        }
      },
      THROTTLE_DELAY,
      THROTTLE_CONFIG
    );

    return () => {
      throttledFocusRef.current?.cancel();
      throttledBlurRef.current?.cancel();
    };
  }, [onKeyboardFocus, onKeyboardBlur]);

  const handleProviderKeyboardFocus = useCallback(
    (provider) => (_event) => {
      if (provider && provider.id && throttledFocusRef.current) {
        throttledFocusRef.current(provider.id);
      }
    },
    []
  );

  const handleProviderKeyboardBlur = useCallback((event) => {
    if (throttledBlurRef.current) {
      throttledBlurRef.current(event?.relatedTarget);
    }
  }, []);

  const handleProviderMouseEnter = useCallback(
    (provider) => (_event) => {
      if (onProviderHover && provider && provider.id) {
        onProviderHover(provider.id);
      }
    },
    [onProviderHover]
  );

  const handleProviderMouseLeave = useCallback(
    (_provider) => (event) => {
      const relatedTarget = event?.relatedTarget;

      if (!relatedTarget || !isProviderElement(relatedTarget)) {
        if (onProviderHover) {
          onProviderHover(null);
        }
      }
    },
    [onProviderHover]
  );

  const handleKeyDown = useCallback(
    (e) => {
      const providerId = getProviderId();
      if (
        (e.key === 'Enter' || e.key === ' ') &&
        onKeyboardFocus &&
        providerId
      ) {
        e.preventDefault();
        onKeyboardFocus(providerId);
      }
    },
    [onKeyboardFocus, getProviderId]
  );

  return {
    handleProviderKeyboardFocus,
    handleProviderKeyboardBlur,
    handleProviderMouseEnter,
    handleProviderMouseLeave,
    handleKeyDown
  };
};
